import type * as zod from 'zod';

import { DEFAULT_RECORDING_USER_ID } from '@sb/ai/constants';
import {
  createMappingFromHardwareKeyToEquipmentInfo,
  createRobotIdsMap,
} from '@sb/ai/helpers';
import type { StandardBotsRO1State } from '@sb/integrations/implementations/StandardBotsRO1/types';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import {
  RecorderStateStatusEnum,
  StopRecordingRequestActionEnum,
  type RecorderConfig,
  type RecorderState,
  type ReferenceFrame,
  type StepDetails,
  type StepDetailsEntry,
  type SaveRecordingRequest,
  type StartRecordingRequest,
  type StopRecordingRequest,
  type StopRecordingResponse,
} from '@sb/sb-robot-sdk/src/codegen/typescript-axios';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { EquipmentStateItem } from '../../RoutineRunnerState';
import type { StepFailure } from '../../types';
import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';
import type { StepKind } from '../StepKind';

import Arguments, { type TagValue } from './Arguments';
import Variables from './Variables';

const log = makeNamespacedLog('CreateRecordingStep');

// Type guard to check if a tag is a TagExpression
const isTagExpression = (
  tag: TagValue,
): tag is Extract<TagValue, { kind: 'TagExpression' }> => {
  return typeof tag !== 'string' && tag.kind === 'TagExpression';
};

// Options offered to the operator when `promptForLabel` is enabled. The array
// order is the display order in the prompt modal, and the modal keys its
// per-choice icon and color off these exact strings.
export const RECORDING_LABEL_CHOICES = [
  'Success',
  'Failure',
  'Discard',
  'Unknown',
] as const;

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

/**
 * Context for recording failure from the primary bot's recorder state
 */
type PrimaryRecordingFailureContext = {
  source: 'primary';
  state?: RecorderState;
};

/**
 * Context for recording failure from a secondary bot
 */
type SecondaryRecordingFailureContext = {
  source: 'secondary';
  equipmentId?: string;
  alias?: string;
  reason?:
    | 'websocket_disconnected'
    | 'routine_failure'
    | 'routine_not_running'
    | 'routine_paused';
  websocketStatus?: string;
  routineRunnerState?: unknown;
};

/**
 * Context for recording failure due to an error during status check
 */
type ErrorRecordingFailureContext = {
  source: 'error';
  error?: unknown;
};

/**
 * Discriminated union of all possible recording failure contexts
 */
type RecordingFailureContext =
  | PrimaryRecordingFailureContext
  | SecondaryRecordingFailureContext
  | ErrorRecordingFailureContext;

/**
 * Generates a human-readable failure reason from a recording failure context
 */
function getRecordingFailureReason(context: RecordingFailureContext): string {
  switch (context.source) {
    case 'primary': {
      const { state } = context;
      let errorsMsg = '';

      if (state?.errors?.length) {
        errorsMsg = `errors: ${state.errors.join(', ')}`;
      }

      return `Recording failed or stopped (status=${state?.status ?? 'unknown'}). ${errorsMsg}`;
    }

    case 'secondary': {
      const botName = context.alias ?? context.equipmentId ?? 'unknown';

      switch (context.reason) {
        case 'websocket_disconnected':
          return `Secondary bot "${botName}" websocket connection lost during recording (status: ${context.websocketStatus ?? 'unknown'})`;
        case 'routine_failure':
          return `Secondary bot "${botName}" routine runner entered failure state during recording`;
        case 'routine_not_running':
          return `Secondary bot "${botName}" routine runner stopped during recording`;
        case 'routine_paused':
          return `Secondary bot "${botName}" routine runner paused during recording`;
        default:
          return `Secondary bot "${botName}" encountered an error during recording`;
      }
    }

    case 'error': {
      const errorMessage =
        (context.error as { message?: string })?.message ?? context.error;

      return `Recording status check failed: ${String(errorMessage)}`;
    }

    default:
      return 'Recording failed for unknown reason';
  }
}

export default class CreateRecordingStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  /**
   * Track whether recording was successfully started
   * @internal
   */
  private recordingInProgress: boolean = false;

  /**
   * Timeout handle for automatic recording termination
   * @internal
   */
  private timeoutHandle: NodeJS.Timeout | null = null;

  /**
   * Recording start timestamp for calculating step offsets
   * @internal
   */
  private recordingStartTime: number = 0;

  /**
   * Monitoring cleanup function
   * @internal
   */
  private cancelMonitoring: undefined | (() => void) = undefined;

  /**
   * True while the routine is paused on the end-of-recording label prompt.
   * @internal
   */
  private awaitingLabel: boolean = false;

  /**
   * The operator's answer to the label prompt, or undefined while unanswered.
   * @internal
   */
  private labelChoice: string | undefined = undefined;

  /**
   * Resolver for the in-flight label wait, so `_stop` can release `_play` when
   * the step is torn down mid-prompt. Without it the play promise would never
   * settle after a stop during the prompt.
   * @internal
   */
  private labelWaitResolve: (() => void) | null = null;

  /**
   * Set when a recording has been stopped with its upload deferred until the
   * operator answers. Holds the recorder's own timestamps for that window,
   * which the follow-up save has to quote back. Non-null means "stopped but
   * not yet uploaded" — the state `_stop` uses to decide whether an unanswered
   * prompt still owes an upload.
   * @internal
   */
  private deferredSave: {
    startTimestamp: number;
    endTimestamp: number;
  } | null = null;

  /**
   * True while the stop that closes a recording for the label prompt is still
   * in flight. During that window the recording counts as neither in progress
   * nor deferred, so `_stop` needs this to know an upload is owed — and that
   * `_play`, still waiting on that same stop, is the one that owes it.
   * @internal
   */
  private deferredStopInFlight: boolean = false;

  /**
   * True once this play has been stopped. The prompt cannot open after that —
   * a stopped routine can no longer be paused, and nobody is there to answer.
   * @internal
   */
  private stopRequested: boolean = false;

  public initializeVariableState(): void {
    this.variables = {};
  }

  /**
   * Whether the end-of-recording prompt is open and unanswered. Gates
   * `acceptsConfirmation` and the `_resume` rejection.
   */
  public isAwaitingLabel(): boolean {
    return this.awaitingLabel && this.labelChoice === undefined;
  }

  /**
   * The label prompt only counts while it is actually open — otherwise a
   * confirm aimed at a recording step would be a resume backdoor around
   * whatever pause the operator is really looking at.
   */
  public acceptsConfirmation(): boolean {
    return this.isAwaitingLabel();
  }

  /**
   * `confirm` releases the label wait immediately, so the runner must check
   * resumability BEFORE accepting the label — a resume that failed its
   * prechecks afterwards would leave the recording uploading while the
   * routine stays paused.
   */
  public readonly confirmationNeedsResumePrechecks: boolean = true;

  /**
   * Receive the operator's end-of-recording label choice. Invoked by
   * `RoutineRunner#confirmStep` while the prompt is open. Anything outside the
   * fixed choice set is rejected: the prompt is required, so a bare or
   * unrecognized confirm must not dismiss it and upload the recording
   * unlabeled.
   */
  public confirm(input?: string): void {
    if (!this.isAwaitingLabel()) {
      throw new Error(
        'CreateRecordingStep is not waiting for a recording label.',
      );
    }

    const choice = RECORDING_LABEL_CHOICES.find(
      (candidate) =>
        candidate.toLowerCase() === (input ?? '').trim().toLowerCase(),
    );

    if (!choice) {
      throw new Error(
        `A recording label is required: choose ${RECORDING_LABEL_CHOICES.join(', ')}.`,
      );
    }

    this.labelChoice = choice;
    this.labelWaitResolve?.();
  }

  /**
   * Reject the generic resume path while the label prompt is open. Without
   * this, pressing the main Resume control would clear the paused state while
   * `_play` stays blocked on the label — the routine would report running but
   * never advance. Mirrors `WaitForConfirmationStep._resume`.
   */
  public _resume(): void {
    if (this.isAwaitingLabel()) {
      throw new Error(
        `Recording is waiting for a label. Choose ${RECORDING_LABEL_CHOICES.join(', ')} to continue.`,
      );
    }
  }

  public hasSelfTimeout(): boolean {
    return false;
  }

  // Subclasses override to dispatch the recorder in a different mode (e.g.
  // CreateDemonstrationStep returns 'demonstration' so echo-bot routes the
  // recording to the demonstration_recordings table instead of skills).
  protected getRecordingMode(): 'standard' | 'demonstration' {
    return 'standard';
  }

  // Subclasses override to attach a reference frame to the recording (e.g.
  // CreateDemonstrationStep resolves the user-selected reference object's
  // world pose at record start so replay can anchor relative to it).
  // Returning a value here — or throwing — happens before the recorder is
  // configured, so an unresolvable reference fails the recording cleanly.
  protected async getReferenceFrame(): Promise<ReferenceFrame | undefined> {
    return undefined;
  }

  public async _setup(_args: StepPlayArguments): Promise<void> {
    const recorderApi = this.getRecorderApi();
    await recorderApi.clearHardwareConfigCache();
  }

  /**
   * Collect static string tags from the arguments
   * @internal
   */
  private collectStaticTags(): string[] {
    const staticTags: string[] = [];

    for (const tag of this.args.tags) {
      if (typeof tag === 'string') {
        staticTags.push(tag);
      }
    }

    return staticTags;
  }

  /**
   * Evaluate expression tags and return the resulting string tags
   * @internal
   */
  private async evaluateExpressionTags(): Promise<string[]> {
    const evaluatedExpressionTags: string[] = [];

    for (const tag of this.args.tags) {
      if (isTagExpression(tag)) {
        try {
          const value = await this.routineFrame.evaluateExpression(
            tag.expression,
            undefined,
            true, // noFinalFailAndThrow - don't fail routine
          );

          // Convert value to string if it's not already
          let stringValue: string;

          if (typeof value === 'string') {
            stringValue = value;
          } else if (value != null) {
            stringValue = String(value);
          } else {
            log.warn(
              'recording.tags',
              'Expression tag evaluated to null/undefined',
              {
                tag,
              },
            );

            continue;
          }

          if (stringValue.trim().length > 0) {
            evaluatedExpressionTags.push(stringValue.slice(0, 30)); // Enforce max length
          }
        } catch (error) {
          log.warn('recording.tags', 'Failed to evaluate expression tag', {
            error,
            tag,
          });
        }
      }
    }

    log.info('recording.tags', 'Evaluated expression tags', {
      evaluatedExpressionTags,
    });

    return evaluatedExpressionTags;
  }

  /**
   * Assemble the tags the recording is stopped with: the routine's own status,
   * any secondary bot's status, and the evaluated expression tags.
   * @internal
   */
  private async collectStopTags(): Promise<string[]> {
    const routineStatus = this.routineContext.getRoutineRunnerState().kind;
    const tags: string[] = [];

    // Add status tags for primary bot
    if (routineStatus === 'Failure') {
      tags.push('error');
    } else if (routineStatus !== 'RoutineRunning') {
      tags.push('stopped');
    }

    // Check secondary bot routine runner states if monitoring is enabled for bimanual tasks
    const secondaryBotTags = this.getSecondaryBotStatusTags();

    // Add secondary bot status tags to the tags array if they're not already present
    for (const tag of secondaryBotTags) {
      if (!tags.includes(tag)) {
        tags.push(tag);
      }
    }

    // Evaluate expression tags if possible (substeps may not have completed)
    const evaluatedExpressionTags = await this.evaluateExpressionTags();

    // Combine status tags and evaluated expression tags
    return [...evaluatedExpressionTags, ...tags];
  }

  public async _stop({
    fail,
  }: Partial<StepPlayArguments> & Pick<StepAction, 'fail'>): Promise<void> {
    // Clear timeout if step is stopped early
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    this.cancelMonitoring?.();

    this.stopRequested = true;

    // Release an open label prompt so the blocked `_play` can unwind. Clearing
    // `awaitingLabel` also stops a torn-down run's prompt state from leaking
    // into the next play of this same step instance.
    this.awaitingLabel = false;
    this.labelWaitResolve?.();
    this.labelWaitResolve = null;

    if (this.deferredStopInFlight) {
      // The recorder is mid-close for a prompt that can no longer open. `_play`
      // is still waiting on that same stop and uploads the recording as
      // 'unknown' as soon as it returns, so leave it there. Waiting for it here
      // would deadlock the failure path: a recorder error is reported through
      // `fail` from inside the request, and that teardown is what calls
      // `_stop`.
      log.info(
        '_stop',
        'Recorder stop still in flight; the play path owes the "unknown" upload',
      );

      return;
    }

    if (this.recordingInProgress) {
      const allTags = await this.collectStopTags();

      // Try to collect step details if substeps have started
      const stepDetails = this.collectStepTimings();

      if (this.shouldPromptForLabel()) {
        // A mid-run stop can never open the label prompt, but the fused
        // stop+upload has no field for a label at all. Close the recorder
        // with the save deferred, then upload with the 'unknown' verdict —
        // the same escape hatch the prompt itself offers.
        await this.stopRecordingDeferred({ tags: allTags, stepDetails, fail });

        await this.saveDeferredRecording({ collectionLabel: 'unknown', fail });

        return;
      }

      await this.stopRecording({ tags: allTags, stepDetails, fail });
    } else if (this.deferredSave && this.labelChoice === undefined) {
      // Reaching here means the stop landed while the label prompt was open:
      // `deferredSave` is only set on the way into `awaitLabelChoice` (no
      // await separates them), and the races where a stop wins before the
      // prompt exists exit through the `deferredStopInFlight` /
      // `stopRequested` paths above and in `stopThenPromptForLabel`. The
      // operator walked away, or the stop raced (and possibly discarded)
      // their answer — the outcome is genuinely unstated, which is exactly
      // what 'unknown' means. Record it so the verdict pipeline holds the
      // session for review instead of it reading as never-prompted, and WARN
      // because a tapped answer may have been thrown away.
      log.warn(
        '_stop',
        'Stop discarded an open label prompt; uploading with label "unknown"',
      );

      await this.saveDeferredRecording({ collectionLabel: 'unknown', fail });
    } else if (!this.deferredSave && this.labelChoice === undefined) {
      // A chosen label means the play path already ran the labeled save and
      // teardown owes nothing — only warn when no recording ever started.
      log.warn(
        '_stop',
        'Skipping stop record because recording is not in progress.',
        { recordingInProgress: this.recordingInProgress },
      );
    }
  }

  /**
   * Whether the operator should be asked to label this recording. The
   * `collectionLabels` flag doubles as a kill switch: turning it off also
   * silences routines whose saved config still carries `promptForLabel`. A
   * recording that is deleted rather than uploaded has no label to carry.
   * @internal
   */
  private shouldPromptForLabel(): boolean {
    return (
      this.args.uploadRecording &&
      this.args.promptForLabel &&
      getFeatureFlagSync('collectionLabels', false)
    );
  }

  /**
   * Close the recorder with the save deferred and capture the recording
   * window for the later `saveDeferredRecording`. Marking the stop as in
   * flight before dispatching it matters on both callers' paths: a `_stop`
   * landing while the recorder is still closing sees no recording in progress
   * and no deferred save yet; without the flag it would take the skip path,
   * and both sides would leave the closed recording with nobody to upload it.
   * By the time this returns the window is over: either the deferred save is
   * set, or the stop failed and there is nothing to upload.
   * @internal
   */
  private async stopRecordingDeferred({
    tags,
    stepDetails,
    fail,
  }: {
    tags: string[];
    stepDetails?: StepDetails;
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<StopRecordingResponse | null> {
    this.deferredStopInFlight = true;

    let stopResponse: StopRecordingResponse | null = null;

    try {
      stopResponse = await this.stopRecording({
        tags,
        stepDetails,
        fail,
        deferSave: true,
      });

      if (stopResponse) {
        this.deferredSave = {
          startTimestamp: Number(stopResponse.startTimestamp ?? 0),
          endTimestamp: Number(stopResponse.endTimestamp ?? 0),
        };
      }
    } finally {
      this.deferredStopInFlight = false;
    }

    return stopResponse;
  }

  /**
   * Close the recorder, ask the operator how the recording went, then upload
   * with their answer. Stopping first is what keeps the operator's thinking
   * time out of the data — prompting while the recorder ran would end every
   * labeled recording with however many seconds of a stationary robot it took
   * to answer.
   * @internal
   */
  private async stopThenPromptForLabel({
    fail,
    pauseRoutine,
  }: Pick<StepPlayArguments, 'fail' | 'pauseRoutine'>): Promise<void> {
    // The recorded motion is over. Cancel the recorder monitor before stopping:
    // it fails the routine whenever the recorder leaves the Recording state,
    // which is exactly what happens next, and a peer bot finishing its own
    // routine while this operator answers is expected rather than a fault.
    this.cancelMonitoring?.();
    this.cancelMonitoring = undefined;

    const tags = await this.collectStopTags();
    const stepDetails = this.collectStepTimings();

    const stopResponse = await this.stopRecordingDeferred({
      tags,
      stepDetails,
      fail,
    });

    if (!stopResponse) {
      // The recording was either never running or failed to stop (already
      // reported through `fail`). There is no closed recording to label, and
      // prompting would strand the operator at a modal that resolves nothing.
      return;
    }

    if (this.stopRequested) {
      // The routine was stopped while the recorder was closing. It can no
      // longer be paused, so there is no prompt to open — the outcome is
      // unstated, which is exactly what 'unknown' means. `saveDeferredRecording`
      // runs at most once, so it does not matter whether `_stop` gets here
      // first.
      log.info(
        'recording.save',
        'Routine stopped while the recorder was closing; saving with label "unknown"',
      );

      await this.saveDeferredRecording({ collectionLabel: 'unknown', fail });

      return;
    }

    try {
      await this.awaitLabelChoice({ pauseRoutine });
    } catch (pauseError) {
      // The routine stopped between closing the recorder and pausing on the
      // prompt, so `pauseRoutine` rejected. That rejection is swallowed by the
      // stop's preemption of `_play`, which would silently lose the recording.
      // Nobody was ever asked, so the verdict is 'unknown'.
      log.warn(
        'recording.save',
        'Could not open the label prompt; saving with label "unknown"',
        { error: pauseError },
      );

      this.awaitingLabel = false;
      this.labelWaitResolve = null;

      await this.saveDeferredRecording({ collectionLabel: 'unknown', fail });

      return;
    }

    if (this.labelChoice === undefined) {
      // `_stop` released the wait because the routine was stopped or torn down
      // mid-prompt; it owns the 'unknown' upload from there.
      return;
    }

    await this.saveDeferredRecording({
      collectionLabel: this.labelChoice.toLowerCase(),
      fail,
    });
  }

  /**
   * Pause the routine on the label prompt and block until the operator answers
   * through `confirm()`, or until `_stop` releases the wait.
   * @internal
   */
  private async awaitLabelChoice({
    pauseRoutine,
  }: Pick<StepPlayArguments, 'pauseRoutine'>): Promise<void> {
    this.labelChoice = undefined;
    this.awaitingLabel = true;

    // Create the wait, and its release handle, BEFORE pausing: a stop landing
    // while `pauseRoutine` is still in flight can then release the wait rather
    // than leave `_play` blocked forever. Promise executors run synchronously,
    // so the resolver is set before `pauseRoutine` starts.
    const labelWait = new Promise<void>((resolve) => {
      this.labelWaitResolve = resolve;
    });

    await pauseRoutine({
      kind: 'waitForConfirmation',
      reason: 'How did this recording go?',
      mode: 'choice',
      choices: [...RECORDING_LABEL_CHOICES],
      required: true,
      // Render the one-tap label buttons rather than the generic
      // choose-then-resume confirmation modal.
      variant: 'recordingLabel',
    });

    if (!this.awaitingLabel) {
      // Torn down while the pause was being applied — nothing left to wait on.
      this.labelWaitResolve = null;

      return;
    }

    await labelWait;

    this.labelWaitResolve = null;
    this.awaitingLabel = false;
  }

  /**
   * Upload a recording that was stopped with its save deferred. Runs at most
   * once per stopped recording: the deferred state is cleared before the
   * request goes out, so a concurrent teardown cannot upload it twice.
   * @internal
   */
  private async saveDeferredRecording({
    collectionLabel,
    fail,
  }: {
    collectionLabel?: string;
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<void> {
    const deferred = this.deferredSave;

    if (!deferred) {
      return;
    }

    this.deferredSave = null;

    try {
      const recorderApi = this.getRecorderApi();

      const saveRequest: SaveRecordingRequest = {
        recordingId: '',
        startTimestamp: deferred.startTimestamp,
        endTimestamp: deferred.endTimestamp,
        collectionLabel,
      };

      const saveResponse = await recorderApi.saveRecording(saveRequest);

      const saveErrors = saveResponse.data?.errors ?? [];

      if (saveErrors.length) {
        // The recorder answers 200 with errors when part of the save did not
        // happen — a secondary bot's upload, or the state lookup that finds the
        // secondaries. Only a rejected request reaches the catch below, so
        // without this a partial save reads as a complete one.
        throw new Error(saveErrors.join('; '));
      }

      log.info('recording.save', 'Saved recording', {
        collectionLabel,
        data: saveResponse.data,
      });
    } catch (saveError) {
      const errorMessage = (saveError as Error)?.message || 'Unknown error';

      // The recorder is already closed, so a failed save is the difference
      // between a stored recording and a lost one. It has to surface.
      await fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: `Failed to save recording: ${errorMessage}`,
        error: saveError as Error,
      });
    }
  }

  public async _play({
    playSubSteps,
    fail,
    pauseRoutine,
  }: StepPlayArguments): Promise<void> {
    // A looped recording step replays on the same instance: clear the previous
    // iteration's answer so this recording cannot inherit its label, and its
    // stop so this iteration still reaches the prompt.
    this.awaitingLabel = false;
    this.labelChoice = undefined;
    this.stopRequested = false;

    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: `${this.getStepKind()} step not supported in visualizer`,
      });
    }

    // Check for bimanual secondary bot state monitoring requirements
    const secondaryBotMonitoringError =
      this.validateSecondaryBotMonitoringRequirements();

    if (secondaryBotMonitoringError) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: secondaryBotMonitoringError,
      });
    }

    try {
      const recorderApi = this.getRecorderApi();

      // Start recording before playing substeps
      await this.startRecording();

      // Store recording start time for calculating step offsets
      this.recordingStartTime = Date.now();

      // Set up timeout if specified
      if (this.args.timeout && this.args.timeout > 0) {
        this.timeoutHandle = setTimeout(async () => {
          log.warn(
            'recording.timeout',
            `Recording timeout reached (${this.args.timeout}s), stopping recording and failing routine`,
            {
              timeout: this.args.timeout,
              taskId: this.args.taskId,
            },
          );

          // Evaluate expression tags before stopping due to timeout
          const evaluatedExpressionTags = await this.evaluateExpressionTags();

          // Combine evaluated expression tags with timeout tag
          const allTags = [...evaluatedExpressionTags, 'timeout'];

          // Try to collect step details if substeps have started
          const stepDetails = this.collectStepTimings();

          if (this.shouldPromptForLabel()) {
            // A timed-out labeled session can never be prompted (the routine
            // is about to fail), and the fused stop+upload has no label
            // field — the same reasoning as the stop paths: defer the save
            // and upload with the 'unknown' verdict so the recording lands
            // in the review queue instead of vanishing from the label
            // filter.
            await this.stopRecordingDeferred({
              tags: allTags,
              stepDetails,
              fail,
            });

            await this.saveDeferredRecording({
              collectionLabel: 'unknown',
              fail,
            });
          } else {
            await this.stopRecording({ tags: allTags, stepDetails, fail });
          }

          // Fail the routine
          await fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: this.getStepKind(),
            },
            failureReason: `Recording timeout exceeded (${this.args.timeout}s)`,
          });
        }, this.args.timeout * 1000); // Convert seconds to milliseconds
      }

      // Status monitoring - in case recording fails
      this.cancelMonitoring = this.startMonitoring(
        async () => {
          try {
            const res = await recorderApi.getRecorderState();

            const state = res.data;

            log.info('recording.status', 'Recording state', {
              state,
            });

            // Check primary bot recording state
            const primaryBotTriggered =
              state?.status !== RecorderStateStatusEnum.Recording &&
              state?.status !== RecorderStateStatusEnum.Initializing;

            if (primaryBotTriggered) {
              return {
                triggered: true,
                context: { source: 'primary', state },
              };
            }

            // Check secondary bot routine runner states if monitoring is enabled
            const secondaryBotIssue =
              this.checkSecondaryBotStatesForMonitoring();

            if (secondaryBotIssue) {
              return secondaryBotIssue;
            }

            return {
              triggered: false,
              context: state,
            };
          } catch (err) {
            // Consider transport/other errors as non-recording
            log.error('recording.status', 'Error fetching recorder state', err);

            return {
              triggered: true,
              context: { source: 'error', error: err },
            };
          }
        },
        {
          onTriggered: async ({ stopMonitoring }, context) => {
            stopMonitoring();

            // Yield briefly so that a concurrent fail() from a substep has
            // time to propagate before we read the routine state.
            await new Promise((resolve) => setTimeout(resolve, 200));

            // If the routine is no longer running (e.g., a substep already
            // called fail(), or the routine was stopped during the delay),
            // don't overwrite with the less-specific recording state error.
            const routineState =
              this.routineContext.getRoutineRunnerState().kind;

            if (routineState !== 'RoutineRunning') {
              log.info(
                'recording.monitoring',
                `Skipping recording failure report — routine is no longer running (state: ${routineState})`,
              );

              return;
            }

            try {
              const failureReason = getRecordingFailureReason(
                context as RecordingFailureContext,
              );

              await fail({
                failure: {
                  kind: FailureKind.StepPlayFailure,
                  stepKind: this.getStepKind(),
                },
                failureReason,
              });
            } catch (err) {
              await fail({
                failure: {
                  kind: FailureKind.StepPlayFailure,
                  stepKind: this.getStepKind(),
                },
                failureReason: `Recording status check failed: ${String((err as any)?.message ?? err)}`,
                error: err as any,
              });
            }
          },
        },
        { pollIntervalMs: 1000 },
      );
    } catch (error) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: `${error?.message}`,
        error,
      });
    }

    await playSubSteps();

    // Clear timeout since substeps completed normally
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    if (this.shouldPromptForLabel()) {
      await this.stopThenPromptForLabel({ fail, pauseRoutine });
    }

    // Recording stop, monitoring stop, etc. all happen gracefully when the step is stopped by routine runner.
  }

  public async _pause({ fail }: StepAction): Promise<void> {
    // The label prompt pauses the routine on purpose, and `pauseRoutine` fans
    // the pause out to every running step — including this one. The recorder is
    // already closed by then, so there is nothing to stop, and falling through
    // would tear the step down and abandon the prompt.
    if (this.awaitingLabel) {
      log.info('_pause', 'Paused for the end-of-recording label prompt');

      return;
    }

    const routineState = this.routineContext.getRoutineRunnerState();

    // Check if this is an "expected" pause that should continue recording
    // Only relevant to primary bot
    if (routineState.kind === 'RoutineRunning') {
      const safeStepKinds: StepKind[] = ['Antigravity', 'WaitForConfirmation'];
      const { currentStepKind } = routineState;

      if (currentStepKind && safeStepKinds.includes(currentStepKind)) {
        log.info(
          '_pause',
          'Pausing but continuing recording (safe substep triggered pause)',
          { currentStepKind },
        );

        return; // Don't stop the recording
      }
    }

    log.warn(
      '_pause',
      'Pause on CreateRecording step is not supported, stopping step.',
    );

    await this._stop({ fail });
  }

  /**
   * Recursively collect all steps (including nested substeps) and their details
   * @param steps - Steps to collect from
   * @param parentStepIds - IDs of parent steps in the hierarchy
   * @returns StepDetails object containing the entries
   */
  private collectAllStepsRecursive(
    steps: Array<Step<object, object>>,
    parentStepIds: string[] = [],
  ): StepDetails {
    const now = Date.now();
    const allEntries: Array<StepDetailsEntry> = [];

    for (const step of steps) {
      // Get parent step IDs (excluding CreateRecording itself)
      const parentIds = parentStepIds.filter((id) => id !== this.id);

      // Serialize step arguments (limit size to avoid huge payloads)
      let stepArgs: string | undefined;

      try {
        const argsJson = JSON.stringify(step.args);

        // Limit to 10KB to avoid huge payloads
        if (argsJson.length <= 10000) {
          stepArgs = argsJson;
        }
      } catch (error) {
        log.warn('recording.stepTimings', 'Failed to serialize step args', {
          stepId: step.id,
          error,
        });
      }

      // Serialize step variables (limit size) - use toJSON() to get clean variables without stepKind tag
      let stepVariables: string | undefined;

      try {
        const { variables } = step.toJSON();

        if (variables && Object.keys(variables).length > 0) {
          const varsJson = JSON.stringify(variables);

          // Limit to 10KB
          if (varsJson.length <= 10000) {
            stepVariables = varsJson;
          }
        }
      } catch (error) {
        log.warn(
          'recording.stepTimings',
          'Failed to serialize step variables',
          {
            stepId: step.id,
            error,
          },
        );
      }

      // Use playHistory to emit one entry per play() invocation (handles loop iterations).
      // Additionally, if the step is currently mid-execution on a new iteration
      // (playStartTime differs from the last completed entry), include the in-progress state.
      const historyEntries: Array<{
        playStartTime: number;
        fullTimeMs: number;
        selfTimeMs: number;
        isCompleted: boolean;
      }> = step.playHistory.map((e) => ({ ...e, isCompleted: true }));

      const lastEntry = historyEntries[historyEntries.length - 1];

      const isInProgress =
        step.playStartTime > 0 &&
        (!lastEntry || step.playStartTime !== lastEntry.playStartTime);

      if (isInProgress) {
        historyEntries.push({
          playStartTime: step.playStartTime,
          fullTimeMs: step.timers.full.getTime(),
          selfTimeMs: step.timers.self.getTime(),
          isCompleted: false,
        });
      }

      for (const entry of historyEntries) {
        // Only include entries that were played during this recording
        if (entry.playStartTime < this.recordingStartTime) {
          continue;
        }

        const startOffsetSec =
          (entry.playStartTime - this.recordingStartTime) / 1000;

        // For completed entries, trust fullTimeMs even when 0 (sub-millisecond steps).
        // Only fall back to wall-clock elapsed time for in-progress entries.
        const elapsedMs =
          entry.fullTimeMs > 0 || entry.isCompleted
            ? entry.fullTimeMs
            : now - entry.playStartTime;

        const endOffsetSec =
          (entry.playStartTime - this.recordingStartTime + elapsedMs) / 1000;

        const selfTimeSec =
          entry.selfTimeMs > 0 || entry.isCompleted
            ? entry.selfTimeMs / 1000
            : undefined;

        allEntries.push({
          stepId: step.id,
          stepKind: step.getStepKind(),
          startOffsetSec,
          endOffsetSec,
          selfTimeSec,
          parentStepIds: parentIds,
          stepArgs,
          stepVariables,
        });
      }

      // Recursively collect nested substeps
      if (step.substeps && step.substeps.length > 0) {
        const nestedDetails = this.collectAllStepsRecursive(step.substeps, [
          ...parentStepIds,
          step.id,
        ]);

        allEntries.push(...nestedDetails.entries);
      }
    }

    return { entries: allEntries };
  }

  /**
   * Collect timing data from substeps (recursively including all nested substeps)
   * @returns StepDetails object containing the entries, or undefined if no timings
   */
  private collectStepTimings(): StepDetails | undefined {
    if (!this.substeps || this.substeps.length === 0) {
      log.info('recording.stepTimings', 'No substeps to collect timing from');

      return undefined;
    }

    const stepDetails = this.collectAllStepsRecursive(this.substeps, [this.id]);

    if (stepDetails.entries.length === 0) {
      log.info('recording.stepTimings', 'No step timings collected');

      return undefined;
    }

    log.info('recording.stepTimings', 'Collected step timings', {
      count: stepDetails.entries.length,
      timings: stepDetails.entries.map((t: StepDetailsEntry) => ({
        stepId: t.stepId,
        stepKind: t.stepKind,
        startOffsetSec: t.startOffsetSec,
        endOffsetSec: t.endOffsetSec,
        selfTimeSec: t.selfTimeSec,
        parentStepIds: t.parentStepIds,
        hasArgs: !!t.stepArgs,
        hasVariables: !!t.stepVariables,
      })),
    });

    return stepDetails;
  }

  private async startRecording() {
    log.info('recording.start', 'Starting recording for CreateRecording step', {
      taskId: this.args.taskId,
    });

    const allEquipmentState = this.routineContext.equipment.getEquipmentState();

    const robotIdsMap = createRobotIdsMap(
      this.args.hardwareConfigEquipmentMapping,
      this.routineContext.robotId || 'local',
      allEquipmentState,
    );

    const equipmentKeyToEquipmentInfo =
      createMappingFromHardwareKeyToEquipmentInfo(
        this.args.hardwareConfigEquipmentMapping,
      );

    const equipmentKeyAssignments = Object.entries(
      equipmentKeyToEquipmentInfo,
    ).reduce(
      (acc, [key, equipmentInfo]) => {
        acc[key] = {
          equipmentId: equipmentInfo.equipmentId,
          ...(equipmentInfo.feed ? { feed: equipmentInfo.feed } : {}),
        };

        return acc;
      },
      {} as Record<string, { equipmentId: string; feed?: string }>,
    );

    const botIds = Object.values(robotIdsMap);

    const secondaryBotIds = botIds.filter(
      (id) => id !== this.routineContext.robotId,
    );

    try {
      const recorderApi = this.getRecorderApi();

      // Only add static string tags at the start - expression tags will be evaluated at the end
      const staticTags = this.collectStaticTags();

      // Enable secondary bots if any are specified in the hardware config mapping
      await this.ensureSecondaryBotsEnabled(recorderApi, secondaryBotIds);

      // First, configure the recording with task details
      const configureRequest: RecorderConfig = {
        taskId: this.args.taskId || '',
        userId: this.args.userId || DEFAULT_RECORDING_USER_ID,
        isSecondary: false,
        bots: secondaryBotIds,
        sensors: [],
        equipmentKeyAssignments,
        tags: ['routine', ...staticTags], // Tag this as a routine recording with static tags
        recordingMode: this.getRecordingMode(),
        referenceFrame: await this.getReferenceFrame(),
      };

      log.info('recording.configure', 'Configuring recording', {
        taskId: this.args.taskId,
        equipmentKeyAssignments,
        tags: ['routine', ...staticTags],
      });

      const configureResponse =
        await recorderApi.updateRecording(configureRequest);

      if (!configureResponse.data.success) {
        const errors =
          configureResponse.data.errors ||
          configureResponse.data.state?.errors ||
          [];

        log.error('recording.configure', 'Failed to configure recording', {
          errors,
        });

        throw new Error(
          `Failed to configure recording: ${Array.isArray(errors) ? errors.join(', ') : String(errors)}`,
        );
      }

      log.info('recording.configure', 'Successfully configured recording');

      // Then start the actual recording
      const recordingStartTime = new Date();

      const startRecordingRequest: StartRecordingRequest = {
        startTime: recordingStartTime.toISOString(),
        equipmentKeyAssignments,
      };

      log.info('recording.start', 'Starting recording via recorder API');

      this.recordingInProgress = true;

      const startRecordingResponse = await recorderApi.startRecording(
        startRecordingRequest,
      );

      const startState = startRecordingResponse.data.state;

      if (!startRecordingResponse.data.success) {
        throw new Error('Failed to start recording via recorder API');
      }

      if (
        !startState ||
        (startState.status !== RecorderStateStatusEnum.Recording &&
          startState.status !== RecorderStateStatusEnum.Initializing)
      ) {
        log.error('recording.start', 'Failed to start recording', {
          status: startState?.status,
          errors: startState?.errors,
        });

        throw new Error(
          `Failed to start recording: ${startState?.status ?? 'unknown'}. errors: ${startState?.errors?.join(', ')}`,
        );
      }

      log.info(
        'recording.start',
        'Successfully started recording via echo-bot',
      );
    } catch (error) {
      log.error(
        'recording.start',
        'Failed to start recording via recorder API',
        error,
      );

      this.recordingInProgress = false;

      throw error;
    }
  }

  /**
   * Ensures that secondary bots specified in the hardware config mapping are enabled
   * for recording, and disables any bots that are enabled but not specified.
   *
   * @param recorderApi - The recorder API instance
   * @param secondaryBotIds - Array of secondary bot IDs to enable
   * @throws Error if any bot enablement/disablement fails or if recorder state cannot be retrieved
   */
  private async ensureSecondaryBotsEnabled(
    recorderApi: ReturnType<typeof this.getRecorderApi>,
    secondaryBotIds: string[],
  ): Promise<void> {
    const currentStateResponse = await recorderApi.getRecorderState();
    const currentState = currentStateResponse.data;
    const enabledBots = currentState?.config?.bots || [];

    // Handle both string[] and RecorderBotDetails[] formats
    const enabledBotIdStrings = enabledBots
      .map((bot: string | { botId?: string; isEnabled?: boolean }) => {
        if (typeof bot === 'string') {
          return bot;
        }

        if (bot.isEnabled) {
          return bot.botId;
        }

        return undefined;
      })
      .filter((id: string | undefined): id is string => Boolean(id));

    log.info('recording.enableBot', 'Checking secondary bots', {
      secondaryBotIds,
      enabledBotIdStrings,
    });

    // Collect bots that need to be enabled
    const botsToEnable = secondaryBotIds.filter(
      (botId) => !enabledBotIdStrings.includes(botId),
    );

    // Log bots that are already enabled
    const alreadyEnabledBots = secondaryBotIds.filter((botId) =>
      enabledBotIdStrings.includes(botId),
    );

    for (const botId of alreadyEnabledBots) {
      log.info(
        'recording.enableBot',
        `Secondary bot ${botId} is already enabled`,
      );
    }

    // Collect bots that need to be disabled
    const botsToDisable = enabledBotIdStrings.filter(
      (botId: string) => !secondaryBotIds.includes(botId),
    );

    // Enable all bots in parallel
    const enablePromises = botsToEnable.map(async (botId) => {
      try {
        await recorderApi.enableRecorderBot({ botId, enabled: true });
        log.info('recording.enableBot', `Enabled secondary bot ${botId}`);
      } catch (enableError) {
        log.error(
          'recording.enableBot',
          `Failed to enable bot ${botId}`,
          enableError,
        );

        throw new Error(
          `Failed to enable secondary bot ${botId} for recording: ${(enableError as Error)?.message ?? String(enableError)}`,
        );
      }
    });

    // Disable all bots in parallel
    const disablePromises = botsToDisable.map(async (botId: string) => {
      try {
        await recorderApi.enableRecorderBot({ botId, enabled: false });
        log.info('recording.enableBot', `Disabled secondary bot ${botId}`);
      } catch (disableError) {
        log.error(
          'recording.enableBot',
          `Failed to disable bot ${botId}`,
          disableError,
        );

        throw new Error(
          `Failed to disable secondary bot ${botId}: ${(disableError as Error)?.message ?? String(disableError)}`,
        );
      }
    });

    // Execute all enable and disable operations in parallel
    try {
      await Promise.all([...enablePromises, ...disablePromises]);
    } catch (error) {
      const errorMessage =
        (error as Error)?.message ?? String(error) ?? 'Unknown error';

      log.error(
        'recording.enableBot',
        'Failed to enable/disable secondary bots',
        {
          error,
          errorMessage,
          botsToEnable,
          botsToDisable,
        },
      );

      throw new Error(
        `Failed to configure secondary bots for bimanual recording: ${errorMessage}`,
      );
    }
  }

  /**
   * Stop the recorder. Returns the recorder's stop response, or null when there
   * was nothing to stop or the stop failed (already reported through `fail`) —
   * callers that follow up with a deferred save need to tell those apart.
   *
   * With `deferSave`, the stop is sent as `action: defer`: the recorder
   * closes the recording but does not upload it, and this step owes a
   * `saveRecording` call quoting the returned timestamps once the operator's
   * label is in hand. A plain `uploadRecording: false` stop also sends
   * `defer` (keep the files local, no upload owed) — the wire shape is the
   * same; the teardown obligation lives in this step's `deferredSave` state,
   * which only the label-prompt path sets.
   */
  private async stopRecording({
    tags = [],
    stepDetails,
    fail,
    deferSave,
  }: {
    tags?: string[];
    stepDetails?: StepDetails;
    fail: (failure: StepFailure) => Promise<void>;
    deferSave?: boolean;
  }): Promise<StopRecordingResponse | null> {
    if (this.recordingInProgress) {
      try {
        const recorderApi = this.getRecorderApi();

        this.recordingInProgress = false;

        const stopRequest: StopRecordingRequest = {
          action:
            this.args.uploadRecording && !deferSave
              ? StopRecordingRequestActionEnum.Upload
              : StopRecordingRequestActionEnum.Defer,
          tags,
          stepDetails,
        };

        const stopResponse = await recorderApi.stopRecording(stopRequest);

        log.info('recording.stop', 'Stopped recording', {
          action: stopRequest.action,
          data: stopResponse.data,
        });

        return stopResponse.data;
      } catch (recordingError) {
        const errorMessage =
          (recordingError as Error)?.message || 'Unknown error';

        await fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: this.getStepKind(),
          },
          failureReason: `Failed to stop recording: ${errorMessage}`,
          error: recordingError as Error,
        });

        return null;
      }
    }

    log.info('recording.stop', 'Recording not started, skipping stop');

    return null;
  }

  /**
   * Gets the secondary bot equipment (StandardBotsRO1) from the hardware config mapping.
   * These are robots that are not the primary robot and are configured as StandardBotsRO1 equipment.
   *
   * @param allEquipmentState - The current state of all equipment
   * @returns Array of equipment items that are secondary bots
   */
  private getSecondaryBotEquipment(): EquipmentStateItem[] {
    const allEquipmentState = this.routineContext.equipment.getEquipmentState();

    const secondaryBotEquipment: EquipmentStateItem[] = [];

    for (const entry of Object.values(
      this.args.hardwareConfigEquipmentMapping,
    )) {
      // Skip the primary robot (robot_0 with no robotEquipmentId)
      if (entry.robotEquipmentId == null) {
        continue;
      }

      const equipment = allEquipmentState.find(
        (device) => device.id === entry.robotEquipmentId,
      );

      if (equipment && equipment.kind === 'StandardBotsRO1') {
        secondaryBotEquipment.push(equipment);
      }
    }

    return secondaryBotEquipment;
  }

  /**
   * Checks secondary bot routine runner states and returns candidate tags to add.
   * Only checks if monitorSecondaryBotState is enabled and the task is bimanual.
   *
   * @returns Set of candidate tags ('error', 'stopped') based on secondary bot states
   */
  private getSecondaryBotStatusTags(): Set<string> {
    const tags = new Set<string>();

    const secondaryBotEquipment = this.getSecondaryBotEquipment();
    const isBimanual = secondaryBotEquipment.length > 0;

    if (!this.args.monitorSecondaryBotState || !isBimanual) {
      return tags;
    }

    for (const equipment of secondaryBotEquipment) {
      const state = equipment.state as StandardBotsRO1State;
      const { routineRunnerState, alias } = state;

      if (routineRunnerState?.kind === 'Failure') {
        tags.add('error');

        log.warn('_stop', 'Secondary bot routine runner in failure state', {
          equipmentId: equipment.id,
          alias,
          routineRunnerState,
        });
      } else if (routineRunnerState?.kind !== 'RoutineRunning') {
        tags.add('stopped');

        log.warn('_stop', 'Secondary bot routine runner not running', {
          equipmentId: equipment.id,
          alias,
          routineRunnerState,
        });
      }
    }

    return tags;
  }

  /**
   * Validates that secondary bot monitoring requirements are met.
   * Checks feature flag and websocket connection status for all secondary bots.
   *
   * @returns Error message if validation fails, null if validation passes
   */
  private validateSecondaryBotMonitoringRequirements(): string | null {
    const secondaryBotEquipment = this.getSecondaryBotEquipment();
    const isBimanual = secondaryBotEquipment.length > 0;

    if (!this.args.monitorSecondaryBotState || !isBimanual) {
      return null;
    }

    // Check that the secondaryBotWebsocketState feature flag is enabled
    if (!getFeatureFlagSync('secondaryBotWebsocketState', false)) {
      return 'Secondary bot state monitoring requires the secondaryBotWebsocketState feature flag to be enabled';
    }

    // Check that all secondary bots have websocket connections established
    for (const equipment of secondaryBotEquipment) {
      const state = equipment.state as StandardBotsRO1State;
      const wsStatus = state.websocketConnectionStatus;

      if (!wsStatus || wsStatus.kind !== 'connected') {
        const statusKind = wsStatus?.kind ?? 'undefined';

        const errorMsg =
          wsStatus?.kind === 'failure'
            ? `: ${(wsStatus as { kind: 'failure'; error: string }).error}`
            : '';

        return `Secondary bot ${state.alias ?? equipment.id} websocket connection is not established (status: ${statusKind}${errorMsg}). Ensure the secondary bot is connected and the websocket connection is active.`;
      }
    }

    log.info(
      '_play',
      'Secondary bot state monitoring enabled for bimanual task',
      {
        secondaryBotCount: secondaryBotEquipment.length,
        secondaryBots: secondaryBotEquipment.map((eq) => ({
          id: eq.id,
          alias: (eq.state as StandardBotsRO1State).alias,
        })),
      },
    );

    return null;
  }

  /**
   * Checks secondary bot states during recording monitoring.
   * Returns a triggered result if any secondary bot has an issue, null otherwise.
   *
   * @returns Triggered result with context if issue found, null if all bots are healthy
   */
  private checkSecondaryBotStatesForMonitoring(): {
    triggered: true;
    context: {
      source: 'secondary';
      equipmentId: string;
      alias?: string;
      reason:
        | 'websocket_disconnected'
        | 'routine_failure'
        | 'routine_not_running'
        | 'routine_paused'
        | undefined;
      websocketStatus?: string;
      routineRunnerState?: unknown;
    };
  } | null {
    if (!this.args.monitorSecondaryBotState) {
      return null;
    }

    for (const equipment of this.getSecondaryBotEquipment()) {
      const botState = equipment.state as StandardBotsRO1State;
      const { routineRunnerState, websocketConnectionStatus, alias } = botState;

      // Check websocket connection status
      if (
        !websocketConnectionStatus ||
        websocketConnectionStatus.kind !== 'connected'
      ) {
        log.warn(
          'recording.status',
          'Secondary bot websocket disconnected during recording',
          {
            equipmentId: equipment.id,
            alias,
            status: websocketConnectionStatus?.kind,
          },
        );

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'websocket_disconnected',
            websocketStatus: websocketConnectionStatus?.kind,
          },
        };
      }

      if (!routineRunnerState) {
        log.warn(
          'recording.status',
          'Secondary bot routine runner state not available',
          {
            equipmentId: equipment.id,
            alias,
          },
        );

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'websocket_disconnected',
          },
        };
      }

      // Check routine runner state for failure
      if (routineRunnerState.kind === 'Failure') {
        log.warn(
          'recording.status',
          'Secondary bot routine runner in failure state',
          {
            equipmentId: equipment.id,
            alias,
            routineRunnerState,
          },
        );

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'routine_failure',
            routineRunnerState,
          },
        };
      }

      // Check if routine runner is not in RoutineRunning state
      if (routineRunnerState.kind !== 'RoutineRunning') {
        log.warn(
          'recording.status',
          'Secondary bot routine runner not running',
          {
            equipmentId: equipment.id,
            alias,
            routineRunnerState,
          },
        );

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'routine_not_running',
            routineRunnerState,
          },
        };
      }

      // Check if routine is paused - allow if paused due to safe step kinds
      if (routineRunnerState.isPaused) {
        const safeStepKinds: StepKind[] = [
          'Antigravity',
          'WaitForConfirmation',
        ];

        const { currentStepKind } = routineRunnerState;

        if (currentStepKind && safeStepKinds.includes(currentStepKind)) {
          log.info(
            'recording.status',
            'Secondary bot paused but continuing recording (safe substep triggered pause)',
            {
              equipmentId: equipment.id,
              alias,
              currentStepKind,
            },
          );

          // Continue to next equipment check, don't trigger failure
          continue;
        }

        log.warn('recording.status', 'Secondary bot routine is paused', {
          equipmentId: equipment.id,
          alias,
          routineRunnerState,
        });

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'routine_paused',
            routineRunnerState,
          },
        };
      }
    }

    return null;
  }

  private getRecorderApi() {
    const { sdk } = this.routineContext;

    if (!sdk) {
      log.error('recording.stop', 'Public API SDK not available');

      throw new Error('Public API SDK not available');
    }

    const recorderApi = sdk.recorder;

    if (!recorderApi) {
      log.error('recording.api', 'Recorder API not available on SDK');

      throw new Error('Recorder API not available on SDK');
    }

    return recorderApi;
  }
}
